import React, { ChangeEvent, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
    Box,
    Button,
    Divider,
    FormControl,
    MenuItem,
    Select,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import AddIcon from '../../../assets/icons/AddIcon'
import { useStyles } from './styles'
import { useDispatch, useSelector } from 'react-redux'
import { getSecurityAsync, getSecurityKey } from '../../../util/security'
import { convertExpressionToDisplayExpression } from '../../../util/expression'
import { selectAlertModalData } from '../../../selectors/alerts'
import { getTimeBucketDisplayName } from '../../../util/timeBuckets'
import AlertExpressionDropdown from '../alertExpressionDropdown/alertExpressionDropdown'
import { AlertCompareTimeBucketRow } from '../alertExpressionDropdown/AlertCompareTimeBucketRow'
import {
    AlertExpression,
    AlertExpression_Type,
    Condition_SecurityAlertCondition,
    Condition_SecurityAlertCondition_Operator,
    UserAlert_Trigger
} from '../../../proto/generated/nexus'
import type { AlertSettingsSubView, ChartAlertSnapshot } from '../alertModal.typings'
import { Indicator } from '../../../proto/generated/indicator'
import { CompositeConditionTree } from './CompositeConditionTree'
import {
    addPeerLeafInGroupAtPath,
    addRootLeaf,
    splitIntoNestedPairAtPath,
    conditionToForestModel,
    countConditionRowsInForest,
    forestModelToCondition,
    getNodeAtPath,
    replacePrimarySecurityAlertCondition,
    updateSecurityAtPath,
    type ConditionForestModel
} from '../../../util/alertConditionTree'
import { setAlertModalData, updateModalUserAlert } from '../../../reducer/alerts.reducer'
import AlertOperatorDropdown from '../alertOperatorDropdown/alertOperatorDropdown'
import {
    findChartSnapshotIndicatorForAlertExpression,
    formatAlertIndicatorCompareValue,
    getChartIndicatorOutputValue,
    getOperatorSource,
    getPrimarySecurityAlertCondition,
    getSelectedValue,
    isAlertExpressionCompareSecurity
} from '../../../util/alerts'
import { selectIndicators } from '../../../selectors/chart'
import AlertTriggerInput from '../alertTriggerInput/alertTriggerInput'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import dayjs, { Dayjs } from 'dayjs'
import AlertMessageInput from '../alertMessageInput/alertMessageInput'
import { FixedExpressionOutputTypes } from '../alertExpressionDropdown/alertExpressionDropdown.typings'
import { TimeBucket_TimeSeries } from '../../../proto/generated/common'
import { ChartType } from '../../../proto/generated/chart'
import { Time } from 'lightweight-charts'
import { userAlertForConditionalEntryModal } from '../../../util/conditionalEntry'
import { selectMultiPaneFullScreen } from '../../../selectors/common'
import { MULTIPANE_FULLSCREEN_MODE_ID } from '../../../assets/constants'
import { alertModalMenuProps } from '../alertModal'

const CUSTOM_EXPIRY_ID = 'custom'

const EMPTY_CHART_SNAPSHOT: ChartAlertSnapshot = {
    securityList: [],
    comparableSecurities: [],
    availableTimeBuckets: [],
    expressionMode: false,
    expression: '',
    indicators: [],
    chartType: ChartType.CANDLESTICK,
    data: {
        open: 0,
        high: 0,
        low: 0,
        close: 0,
        time: 0 as Time,
        customValues: { volume: 0 },
    },
    timeBucket: { timeSeries: TimeBucket_TimeSeries.SECOND, value: 1 },
}

type PresetExpirationOption = {
    id: string
    label: string
    secondary: (now: Dayjs) => string
    resolveExpiry: (now: Dayjs) => Dayjs
}

/** Preset rows (order matches product spec). Custom is a separate menu item. */
const PRESET_EXPIRATION_OPTIONS: PresetExpirationOption[] = [
    {
        id: 'open_ended',
        label: 'Open-ended',
        secondary: () => "Won't expire",
        resolveExpiry: (now) => now.add(100, 'year').second(0).millisecond(0)
    },
    {
        id: 'end_of_day',
        label: 'End of day',
        secondary: (now) => now.endOf('day').format('MMM D, HH:mm'),
        resolveExpiry: (now) => now.endOf('day')
    },
    {
        id: '1_week',
        label: '1 week',
        secondary: (now) => now.add(1, 'week').second(0).millisecond(0).format('MMM D, HH:mm'),
        resolveExpiry: (now) => now.add(1, 'week').second(0).millisecond(0)
    },
    {
        id: '1_month',
        label: '1 month',
        secondary: (now) => now.add(1, 'month').second(0).millisecond(0).format('MMM D, HH:mm'),
        resolveExpiry: (now) => now.add(1, 'month').second(0).millisecond(0)
    }
]

/** Shared with main condition editor and add/edit composite leaf draft — keeps RHS in sync when LHS changes. */
function applyLhsExpressionChange(
    prev: Condition_SecurityAlertCondition,
    exp: AlertExpression,
    indicators: Indicator[],
    chartSnapshot: ChartAlertSnapshot
): Condition_SecurityAlertCondition {
    const { operator, rhsExpression } = prev
    if (!operator || !rhsExpression) {
        return { ...prev, lhsExpression: exp }
    }

    const condition: Condition_SecurityAlertCondition = {
        lhsExpression: exp,
        operator,
        rhsExpression
    }
    const lhsValue = getSelectedValue(exp, indicators, { chartSnapshot, mode: 'lhs' })

    if (lhsValue[0] === FixedExpressionOutputTypes.PRICE.valueOf()) {
        if (exp.simpleSecurity) {
            exp.simpleSecurity.source = getOperatorSource(operator)
        } else if (exp.spreadSecurity) {
            exp.spreadSecurity.source = getOperatorSource(operator)
        }
        exp.timeBucket = {
            timeSeries: TimeBucket_TimeSeries.SECOND,
            value: 1
        }
    }

    const rhsValue = getSelectedValue(rhsExpression, indicators, { chartSnapshot, mode: 'rhs' })

    if (rhsValue[0] === lhsValue[0] && rhsValue[1] === lhsValue[1]) {
        const indicator = findChartSnapshotIndicatorForAlertExpression(rhsExpression.indicator, chartSnapshot, indicators)
        if (indicator && indicator.outputs.length > 0) {
            const newOutput = indicator.outputs.filter((o) => o.code !== rhsExpression.indicator!.output)[0]
            condition.rhsExpression = {
                type: newOutput ? rhsExpression.type : AlertExpression_Type.FIXED_VALUE,
                fixedValue: !newOutput ? 0 : undefined,
                indicator: newOutput
                    ? {
                          ...rhsExpression.indicator!,
                          output: newOutput.code
                      }
                    : undefined,
                timeBucket: undefined
            }
        }
    }

    if (condition.rhsExpression!.type === AlertExpression_Type.FIXED_VALUE) {
        if (lhsValue[0] === FixedExpressionOutputTypes.PRICE.valueOf()) {
            condition.rhsExpression = {
                ...condition.rhsExpression!,
                fixedValue: chartSnapshot.data.close
            }
        } else if (lhsValue[0] === FixedExpressionOutputTypes.VOLUME.valueOf()) {
            condition.rhsExpression = {
                ...condition.rhsExpression!,
                fixedValue: (chartSnapshot.data.customValues?.volume as number) ?? 0
            }
        } else if (lhsValue[0] === FixedExpressionOutputTypes.COMPARE_SECURITY) {
            /* numeric RHS unchanged */
        } else if (exp.type === AlertExpression_Type.TECHNICAL_INDICATOR && exp.indicator?.indicatorCode) {
            const chartIndicator = findChartSnapshotIndicatorForAlertExpression(exp.indicator, chartSnapshot, indicators)
            const fixedValue = getChartIndicatorOutputValue(chartIndicator, lhsValue[1], indicators)
            condition.rhsExpression = {
                ...condition.rhsExpression!,
                fixedValue: formatAlertIndicatorCompareValue(fixedValue, exp.indicator?.indicatorCode, indicators)
            }
        }
    }

    return condition
}

function applyLhsOutputChange(
    prev: Condition_SecurityAlertCondition,
    output: string,
    indicators: Indicator[],
    chartSnapshot: ChartAlertSnapshot
): Condition_SecurityAlertCondition {
    const { lhsExpression, rhsExpression, operator } = prev
    if (!lhsExpression || !rhsExpression || !operator) {
        return prev
    }

    const condition: Condition_SecurityAlertCondition = {
        lhsExpression: {
            ...lhsExpression,
            indicator: {
                ...lhsExpression.indicator!,
                output
            }
        },
        operator,
        rhsExpression
    }

    const lhsValue = getSelectedValue(condition.lhsExpression!, indicators, { chartSnapshot, mode: 'lhs' })

    if (condition.rhsExpression!.type === AlertExpression_Type.TECHNICAL_INDICATOR) {
        const rhsValue = getSelectedValue(rhsExpression, indicators, { chartSnapshot, mode: 'rhs' })

        if (rhsValue[0] === lhsValue[0] && rhsValue[1] === output) {
            const indicator = findChartSnapshotIndicatorForAlertExpression(rhsExpression.indicator, chartSnapshot, indicators)
            if (indicator) {
                const newOutput = indicator.outputs.filter((o) => o.code !== output)[0]
                condition.rhsExpression = {
                    ...condition.rhsExpression!,
                    indicator: newOutput
                        ? {
                              ...rhsExpression.indicator!,
                              output: newOutput.code
                          }
                        : undefined,
                    timeBucket: undefined
                }
            }
        }
    }

    if (condition.rhsExpression!.type === AlertExpression_Type.FIXED_VALUE) {
        if (lhsValue[0] === FixedExpressionOutputTypes.PRICE) {
            condition.rhsExpression = {
                ...condition.rhsExpression!,
                fixedValue: chartSnapshot.data.close
            }
        } else if (lhsValue[0] === FixedExpressionOutputTypes.COMPARE_SECURITY) {
            /* unchanged */
        } else if (condition.lhsExpression?.indicator?.indicatorCode) {
            const chartIndicator = findChartSnapshotIndicatorForAlertExpression(
                condition.lhsExpression.indicator,
                chartSnapshot,
                indicators
            )
            const fixedValue = getChartIndicatorOutputValue(chartIndicator, lhsValue[1], indicators)
            condition.rhsExpression = {
                ...condition.rhsExpression!,
                fixedValue: formatAlertIndicatorCompareValue(
                    fixedValue,
                    condition.lhsExpression.indicator?.indicatorCode,
                    indicators
                )
            }
        }
    }

    return condition
}

function applyRhsExpressionChange(
    prev: Condition_SecurityAlertCondition,
    exp: AlertExpression,
    change: keyof AlertExpression,
    indicators: Indicator[],
    chartSnapshot: ChartAlertSnapshot
): Condition_SecurityAlertCondition {
    const { lhsExpression, operator } = prev
    if (!operator || !lhsExpression) {
        return { ...prev, rhsExpression: exp }
    }

    const condition: Condition_SecurityAlertCondition = {
        lhsExpression,
        operator,
        rhsExpression: exp
    }

    if (exp.type === AlertExpression_Type.FIXED_VALUE && change !== 'fixedValue') {
        const lhsValue = getSelectedValue(lhsExpression, indicators, { chartSnapshot, mode: 'lhs' })
        if (lhsValue[0] === FixedExpressionOutputTypes.PRICE.valueOf()) {
            condition.rhsExpression = {
                ...exp,
                fixedValue: chartSnapshot.data.close
            }
        } else if (lhsValue[0] === FixedExpressionOutputTypes.VOLUME.valueOf()) {
            condition.rhsExpression = {
                ...exp,
                fixedValue: (chartSnapshot.data.customValues?.volume as number) ?? 0
            }
        } else if (lhsValue[0] === FixedExpressionOutputTypes.COMPARE_SECURITY) {
            /* keep exp.fixedValue from dropdown */
        } else if (lhsExpression.indicator?.indicatorCode) {
            const chartIndicator = findChartSnapshotIndicatorForAlertExpression(
                lhsExpression.indicator,
                chartSnapshot,
                indicators
            )
            const fixedValue = getChartIndicatorOutputValue(chartIndicator, lhsValue[1], indicators)
            condition.rhsExpression = {
                ...exp,
                fixedValue: formatAlertIndicatorCompareValue(fixedValue, exp.indicator?.indicatorCode, indicators)
            }
        }
    }

    return condition
}

export type AlertSettingsTabProps = {
    onValidation: (error: boolean) => void
    settingsSubView?: AlertSettingsSubView | null
    onSettingsSubViewChange?: (next: AlertSettingsSubView | null) => void
    onConditionSubViewActions?: (actions: { save: () => void; saveLabel: string } | null) => void
}

const AlertSettingsTab: React.FC<AlertSettingsTabProps> = ({
    onValidation,
    settingsSubView = null,
    onSettingsSubViewChange = () => {},
    onConditionSubViewActions = () => {}
}) => {
    const dispatch = useDispatch()
    const { classes } = useStyles()
    const multiPaneFullScreen = useSelector(selectMultiPaneFullScreen)
    const indicators = useSelector(selectIndicators)
    const alertModalState = useSelector(selectAlertModalData)
    const chartSnapshot = alertModalState?.chartSnapshot ?? EMPTY_CHART_SNAPSHOT
    const {
        alert,
        edit,
        purpose,
        conditionalModalChartVariants,
        conditionalModalChartRef,
    } = alertModalState ?? {}
    const hideAlertMetadata =
        purpose === 'conditionalEntry' ||
        purpose === 'conditionalExit' ||
        purpose === 'conditionalModify'

    const onConditionalModalChartRefChange = useCallback(
        (_: unknown, v: 'underlying' | 'spread' | null) => {
            if (!v || !alertModalState?.conditionalModalChartVariants) return
            const { underlying, spread } = alertModalState.conditionalModalChartVariants
            const nextSnap = v === 'spread' && spread ? spread : underlying
            dispatch(
                setAlertModalData({
                    ...alertModalState,
                    chartSnapshot: nextSnap,
                    conditionalModalChartRef: v,
                    alert: userAlertForConditionalEntryModal(nextSnap, undefined),
                })
            )
        },
        [dispatch, alertModalState]
    )

    const { securityList, expression, expressionMode, timeBucket } = chartSnapshot
    const [primarySecurity, setPrimarySecurity] = useState<Awaited<ReturnType<typeof getSecurityAsync>>>(undefined)
    const [expressionSecurities, setExpressionSecurities] = useState<Awaited<ReturnType<typeof getSecurityAsync>>[]>([])
    const [draftCondition, setDraftCondition] = useState<Condition_SecurityAlertCondition | undefined>(undefined)
    const alertConditionRef = useRef(alert?.condition)
    const draftConditionRef = useRef<Condition_SecurityAlertCondition | undefined>(undefined)

    alertConditionRef.current = alert?.condition
    draftConditionRef.current = draftCondition

    const [selectedExpiryOption, setSelectedExpiryOption] = useState<string>('1_month')
    const [customExpiryPickerOpen, setCustomExpiryPickerOpen] = useState(false)
    useEffect(() => {
        if (!securityList.length) {
            setPrimarySecurity(undefined)
            setExpressionSecurities([])
            return
        }
        if (!expressionMode) {
            getSecurityAsync(getSecurityKey(securityList[0].exchange, securityList[0].code)).then(setPrimarySecurity)
            setExpressionSecurities([])
        } else {
            Promise.all(securityList.map((v) => getSecurityAsync(getSecurityKey(v.exchange, v.code)))).then(setExpressionSecurities)
            setPrimarySecurity(undefined)
        }
    }, [securityList, expressionMode])

    const symbolsDisplayValue = useMemo(() => {
        if (!securityList.length) return ''
        let elements = ''
        if (!expressionMode && primarySecurity) {
            elements += primarySecurity.symbol
        } else if (expressionMode && expression.length && expressionSecurities.length) {
            elements += convertExpressionToDisplayExpression(expression, expressionSecurities)
        }
        if (!elements.length) return ''
        elements += `, ${getTimeBucketDisplayName(timeBucket)}`
        return elements
    }, [securityList, expression, expressionMode, timeBucket, primarySecurity, expressionSecurities])

    const primaryCondition = useMemo(
        () => getPrimarySecurityAlertCondition(alert?.condition),
        [alert?.condition]
    )
    const lhsExpression = primaryCondition?.lhsExpression
    const rhsExpression = primaryCondition?.rhsExpression
    const operator = primaryCondition?.operator

    const forestModel = useMemo(() => conditionToForestModel(alert?.condition), [alert?.condition])
    const conditionRowCount = useMemo(() => countConditionRowsInForest(forestModel), [forestModel])
    const showSingleConditionEditor = conditionRowCount <= 1

    const onForestChange = useCallback(
        (nextOrUpdater: ConditionForestModel | ((prev: ConditionForestModel) => ConditionForestModel)) => {
            const prev = conditionToForestModel(alertConditionRef.current)
            const next = typeof nextOrUpdater === 'function' ? nextOrUpdater(prev) : nextOrUpdater
            const nextCondition = forestModelToCondition(next)
            if (nextCondition) {
                dispatch(updateModalUserAlert({ condition: nextCondition }))
            }
        },
        [dispatch]
    )

    const onAddConditionClick = useCallback(() => {
        if (!lhsExpression || !rhsExpression || !operator) return
        setDraftCondition({
            lhsExpression: { ...lhsExpression },
            rhsExpression: { ...rhsExpression },
            operator
        })
        onSettingsSubViewChange({ kind: 'addCondition', childPath: null })
    }, [lhsExpression, rhsExpression, operator, onSettingsSubViewChange])

    const onAddChildConditionRequest = useCallback(
        (
            path: number[],
            addIntent: 'siblingUnderGroup' | 'nestedUnderBranchHead' = 'siblingUnderGroup'
        ) => {
            if (!lhsExpression || !rhsExpression || !operator) return
            setDraftCondition({
                lhsExpression: { ...lhsExpression },
                rhsExpression: { ...rhsExpression },
                operator
            })
            const childPath = path.length === 0 ? null : [...path]
            onSettingsSubViewChange({
                kind: 'addCondition',
                childPath,
                addIntent: path.length === 0 ? undefined : addIntent
            })
        },
        [lhsExpression, rhsExpression, operator, onSettingsSubViewChange]
    )

    const onEditConditionRequest = useCallback(
        (path: number[]) => {
            const forest = conditionToForestModel(alertConditionRef.current)
            const node = getNodeAtPath(forest, path)
            const s = node?.security
            if (!s?.lhsExpression || !s.rhsExpression) return
            setDraftCondition({
                lhsExpression: { ...s.lhsExpression },
                rhsExpression: { ...s.rhsExpression },
                operator: s.operator
            })
            onSettingsSubViewChange({ kind: 'editCondition', editPath: [...path] })
        },
        [onSettingsSubViewChange]
    )

    const updateDraftCondition = useCallback((next: Condition_SecurityAlertCondition) => {
        setDraftCondition(next)
    }, [])

    const onConditionFormSave = useCallback(() => {
        const condition = alertConditionRef.current
        const draft = draftConditionRef.current
        if (!condition || !draft) return
        const forest = conditionToForestModel(condition)
        let nextForest: ConditionForestModel
        if (settingsSubView?.kind === 'editCondition') {
            nextForest = updateSecurityAtPath(forest, settingsSubView.editPath, draft)
        } else if (settingsSubView?.kind === 'addCondition') {
            const childPath = settingsSubView.childPath
            if (childPath === null) {
                nextForest = addRootLeaf(forest, draft)
            } else if (settingsSubView.addIntent === 'nestedUnderBranchHead') {
                nextForest = splitIntoNestedPairAtPath(forest, childPath, draft)
            } else {
                nextForest = addPeerLeafInGroupAtPath(forest, childPath, draft)
            }
        } else {
            return
        }
        const nextCondition = forestModelToCondition(nextForest)
        if (nextCondition) {
            dispatch(updateModalUserAlert({ condition: nextCondition }))
        }
        onSettingsSubViewChange(null)
    }, [dispatch, onSettingsSubViewChange, settingsSubView])

    const onDraftOperatorChange = useCallback(
        (nextOperator: Condition_SecurityAlertCondition_Operator) => {
            if (!draftCondition?.lhsExpression || !draftCondition?.rhsExpression) return
            updateDraftCondition({ ...draftCondition, operator: nextOperator })
        },
        [draftCondition, updateDraftCondition]
    )

    const onDraftLhsExpressionChange = useCallback(
        (exp: AlertExpression, _change: keyof AlertExpression) => {
            if (!draftCondition?.rhsExpression || !draftCondition.operator) return
            updateDraftCondition(applyLhsExpressionChange(draftCondition, exp, indicators, chartSnapshot))
        },
        [draftCondition, updateDraftCondition, indicators, chartSnapshot]
    )

    const onDraftRhsExpressionChange = useCallback(
        (exp: AlertExpression, change: keyof AlertExpression) => {
            if (!draftCondition?.lhsExpression) return
            updateDraftCondition(applyRhsExpressionChange(draftCondition, exp, change, indicators, chartSnapshot))
        },
        [draftCondition, updateDraftCondition, indicators, chartSnapshot]
    )

    const onDraftLhsOutputChange = useCallback(
        (output: string) => {
            if (!draftCondition?.lhsExpression?.indicator || !draftCondition.rhsExpression || !draftCondition.operator) {
                return
            }
            updateDraftCondition(applyLhsOutputChange(draftCondition, output, indicators, chartSnapshot))
        },
        [draftCondition, updateDraftCondition, indicators, chartSnapshot]
    )

    const onDraftRhsOutputChange = useCallback(
        (output: string) => {
            if (!draftCondition?.rhsExpression?.indicator) return
            updateDraftCondition({
                ...draftCondition,
                rhsExpression: {
                    ...draftCondition.rhsExpression,
                    indicator: { ...draftCondition.rhsExpression.indicator!, output }
                }
            })
        },
        [draftCondition, updateDraftCondition]
    )

    const onLhsExpressionChange = useCallback(
        (exp: AlertExpression, _change: keyof AlertExpression) => {
            if (!rhsExpression || !operator) return
            const nextSac = applyLhsExpressionChange(
                { lhsExpression: exp, operator, rhsExpression },
                exp,
                indicators,
                chartSnapshot
            )
            dispatch(
                updateModalUserAlert({
                    condition: replacePrimarySecurityAlertCondition(alert?.condition, nextSac),
                })
            )
        },
        [rhsExpression, operator, indicators, chartSnapshot, dispatch, alert?.condition]
    )

    const onRhsExpressionChange = useCallback(
        (exp: AlertExpression, change: keyof AlertExpression) => {
            if (!operator || !lhsExpression || !rhsExpression) return
            const nextSac = applyRhsExpressionChange(
                { lhsExpression, operator, rhsExpression },
                exp,
                change,
                indicators,
                chartSnapshot
            )
            dispatch(
                updateModalUserAlert({
                    condition: replacePrimarySecurityAlertCondition(alert?.condition, nextSac),
                })
            )
        },
        [lhsExpression, operator, rhsExpression, indicators, chartSnapshot, dispatch, alert?.condition]
    )

    const onOperatorChange = useCallback((operator: Condition_SecurityAlertCondition_Operator) => {
        if (!operator || !lhsExpression || !rhsExpression) return
        const lhsValue = getSelectedValue(lhsExpression, indicators, { chartSnapshot, mode: 'lhs' })
        const newLhsExpression = { ...lhsExpression }
        if (lhsValue[0] === FixedExpressionOutputTypes.PRICE) {
            if (newLhsExpression.type === AlertExpression_Type.SIMPLE_SECURITY) {
                newLhsExpression.simpleSecurity = {
                    ...lhsExpression.simpleSecurity!,
                    source: getOperatorSource(operator)
                }
            } else if (newLhsExpression.type === AlertExpression_Type.SPREAD_SECURITY) {
                newLhsExpression.spreadSecurity = {
                    ...lhsExpression.spreadSecurity!,
                    source: getOperatorSource(operator)
                }
            }
        }
        const nextSac: Condition_SecurityAlertCondition = {
            lhsExpression: newLhsExpression,
            operator,
            rhsExpression
        }

        dispatch(
            updateModalUserAlert({
                condition: replacePrimarySecurityAlertCondition(alert?.condition, nextSac),
            })
        )
    }, [lhsExpression, rhsExpression, indicators, chartSnapshot, dispatch, alert?.condition])

    const onLhsOutputChange = useCallback(
        (output: string) => {
            if (!lhsExpression || !rhsExpression || !operator) return
            const nextSac = applyLhsOutputChange(
                { lhsExpression, rhsExpression, operator },
                output,
                indicators,
                chartSnapshot
            )
            dispatch(
                updateModalUserAlert({
                    condition: replacePrimarySecurityAlertCondition(alert?.condition, nextSac),
                })
            )
        },
        [lhsExpression, rhsExpression, operator, indicators, chartSnapshot, dispatch, alert?.condition]
    )

    const onRhsOutputChange = useCallback((output: string) => {
        if (!lhsExpression || !rhsExpression || !operator) return

        const nextSac: Condition_SecurityAlertCondition = {
            operator,
            lhsExpression,
            rhsExpression: {
                ...rhsExpression,
                indicator: {
                    ...rhsExpression.indicator!,
                    output
                }
            }
        }
        dispatch(
            updateModalUserAlert({
                condition: replacePrimarySecurityAlertCondition(alert?.condition, nextSac),
            })
        )
    }, [lhsExpression, rhsExpression, operator, dispatch, alert?.condition])

    const onTriggerChange = useCallback((trigger: UserAlert_Trigger) => {
        dispatch(updateModalUserAlert({
            trigger
        }))
    }, [])

    const onMessageChange = useCallback((message: string) => {
        dispatch(updateModalUserAlert({
            message
        }))
    }, [])

    const onNameChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        dispatch(updateModalUserAlert({
            name: event.target.value
        }))
    }, [])

    const onDateError = useCallback(() => onValidation(true), [])
    const onDateAccept = useCallback(() => onValidation(false), [])

    useEffect(() => {
        if (!alert?.expiry) return
        const now = dayjs()
        const expiry = dayjs(alert.expiry)
        if (expiry.isAfter(now.add(30, 'year'))) {
            setSelectedExpiryOption('open_ended')
            return
        }
        let closestId = '1_month'
        let closestDiff = Infinity
        for (const opt of PRESET_EXPIRATION_OPTIONS) {
            if (opt.id === 'open_ended') continue
            const candidate = opt.resolveExpiry(now)
            const candidateDiff = Math.abs(candidate.diff(expiry, 'minute'))
            if (candidateDiff < closestDiff) {
                closestDiff = candidateDiff
                closestId = opt.id
            }
        }
        // Arbitrary custom time if no preset is within 6 hours of the stored expiry
        if (closestDiff <= 6 * 60) {
            setSelectedExpiryOption(closestId)
        } else {
            setSelectedExpiryOption(CUSTOM_EXPIRY_ID)
        }
    }, [alert?.expiry])

    const onExpiryOptionChange = useCallback(
        (value: string) => {
            if (value === CUSTOM_EXPIRY_ID) {
                setSelectedExpiryOption(CUSTOM_EXPIRY_ID)
                window.setTimeout(() => setCustomExpiryPickerOpen(true), 0)
                return
            }
            const option = PRESET_EXPIRATION_OPTIONS.find((opt) => opt.id === value)
            if (!option) return
            setSelectedExpiryOption(option.id)
            const date = option.resolveExpiry(dayjs())
            onDateAccept()
            dispatch(
                updateModalUserAlert({
                    expiry: date.valueOf()
                })
            )
        },
        [dispatch, onDateAccept]
    )

    const onCustomExpiryAccept = useCallback(
        (value: Dayjs | null) => {
            if (!value || !value.isValid()) return
            onDateAccept()
            dispatch(updateModalUserAlert({ expiry: value.valueOf() }))
        },
        [dispatch, onDateAccept]
    )


    const renderCompareTimeBucketRow = useCallback(
        (
            lhs: AlertExpression | undefined,
            rhs: AlertExpression | undefined,
            op: Condition_SecurityAlertCondition_Operator | undefined,
            onLhsChange: (exp: AlertExpression, change: keyof AlertExpression) => void,
            onRhsChange: (exp: AlertExpression, change: keyof AlertExpression) => void
        ) => {
            if (!lhs || !rhs || !op) return null
            const rhsCmp = isAlertExpressionCompareSecurity(rhs, indicators, chartSnapshot, 'rhs')
            const lhsCmp = isAlertExpressionCompareSecurity(lhs, indicators, chartSnapshot, 'lhs')
            if (!rhsCmp && !lhsCmp) return null
            if (rhsCmp) {
                return (
                    <AlertCompareTimeBucketRow
                        className={classes.row}
                        chartSnapshot={chartSnapshot}
                        expression={rhs}
                        operator={op}
                        onExpressionChange={onRhsChange}
                    />
                )
            }
            return (
                <AlertCompareTimeBucketRow
                    className={classes.row}
                    chartSnapshot={chartSnapshot}
                    expression={lhs}
                    operator={op}
                    onExpressionChange={onLhsChange}
                />
            )
        },
        [chartSnapshot, indicators, classes.row]
    )

    useEffect(() => {
        const isConditionForm =
            (settingsSubView?.kind === 'addCondition' || settingsSubView?.kind === 'editCondition') &&
            draftCondition?.lhsExpression &&
            draftCondition?.rhsExpression

        if (isConditionForm) {
            onConditionSubViewActions({
                save: onConditionFormSave,
                saveLabel: settingsSubView!.kind === 'editCondition' ? 'Save' : 'Add',
            })
        } else {
            onConditionSubViewActions(null)
        }
        return () => onConditionSubViewActions(null)
    }, [settingsSubView, draftCondition, onConditionFormSave, onConditionSubViewActions])

    if (
        (settingsSubView?.kind === 'addCondition' || settingsSubView?.kind === 'editCondition') &&
        draftCondition?.lhsExpression &&
        draftCondition?.rhsExpression
    ) {
        return (
            <Box>
                <Stack mt={1}>
                    <AlertExpressionDropdown
                        isConditionalOrder={hideAlertMetadata}
                        expression={draftCondition.lhsExpression}
                        chartSnapshot={chartSnapshot}
                        comparedExpression={draftCondition.rhsExpression}
                        mode="lhs"
                        operator={draftCondition.operator}
                        onExpressionChange={onDraftLhsExpressionChange}
                        onOutputChange={onDraftLhsOutputChange}
                        className={classes.row}
                    />
                    <AlertOperatorDropdown
                        onOperatorChange={onDraftOperatorChange}
                        className={classes.row}
                        value={draftCondition.operator}
                    />
                    <AlertExpressionDropdown
                        isConditionalOrder={hideAlertMetadata}
                        expression={draftCondition.rhsExpression}
                        chartSnapshot={chartSnapshot}
                        comparedExpression={draftCondition.lhsExpression}
                        mode="rhs"
                        operator={draftCondition.operator}
                        onExpressionChange={onDraftRhsExpressionChange}
                        onOutputChange={onDraftRhsOutputChange}
                    />
                    {renderCompareTimeBucketRow(
                        draftCondition.lhsExpression,
                        draftCondition.rhsExpression,
                        draftCondition.operator,
                        onDraftLhsExpressionChange,
                        onDraftRhsExpressionChange
                    )}
                </Stack>
            </Box>
        )
    }

    return <Box>
        {hideAlertMetadata && conditionalModalChartVariants?.spread && (
            <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                    Condition chart
                </Typography>
                <ToggleButtonGroup
                    exclusive
                    size="small"
                    value={conditionalModalChartRef ?? 'underlying'}
                    onChange={onConditionalModalChartRefChange}
                    sx={{
                        '& .MuiToggleButton-root': {
                            py: 0.35,
                            px: 1,
                            fontSize: '0.75rem',
                            textTransform: 'none',
                        },
                    }}
                >
                    <ToggleButton value="spread">Strategy (spread)</ToggleButton>
                    <ToggleButton value="underlying">Underlying</ToggleButton>
                </ToggleButtonGroup>
            </Stack>
        )}
        <Stack direction="row">
            <label className={classes.label}>Condition</label>
            {operator && lhsExpression && rhsExpression && (
                <Box className={classes.value}>
                    {showSingleConditionEditor && (
                        <>
                            <AlertExpressionDropdown
                                isConditionalOrder={hideAlertMetadata}
                                expression={lhsExpression}
                                chartSnapshot={chartSnapshot}
                                comparedExpression={rhsExpression}
                                mode="lhs"
                                operator={operator}
                                onExpressionChange={onLhsExpressionChange}
                                onOutputChange={onLhsOutputChange}
                                className={classes.row}
                            />
                            <AlertOperatorDropdown
                                onOperatorChange={onOperatorChange}
                                className={classes.row}
                                value={operator}
                            />
                            <AlertExpressionDropdown
                                isConditionalOrder={hideAlertMetadata}
                                expression={rhsExpression}
                                chartSnapshot={chartSnapshot}
                                comparedExpression={lhsExpression}
                                mode="rhs"
                                operator={operator}
                                onExpressionChange={onRhsExpressionChange}
                                onOutputChange={onRhsOutputChange}
                            />
                            {renderCompareTimeBucketRow(
                                lhsExpression,
                                rhsExpression,
                                operator,
                                onLhsExpressionChange,
                                onRhsExpressionChange
                            )}
                        </>
                    )}
                    {!showSingleConditionEditor && (
                        <Box sx={{ ml: -1.25 }}>
                            <CompositeConditionTree
                                forest={forestModel}
                                onForestChange={onForestChange}
                                onAddChildRequest={onAddChildConditionRequest}
                                onEditConditionRequest={onEditConditionRequest}
                                symbolsDisplayValue={symbolsDisplayValue}
                                indicators={indicators}
                                messageContext={{
                                    chartSnapshot,
                                    chartTimeBucket: alert.timeBucket ?? chartSnapshot.timeBucket,
                                }}
                            />
                        </Box>
                    )}
                    {showSingleConditionEditor && (
                        <Box display="flex" justifyContent="flex-start" mt={1}>
                            <Button
                                size="small"
                                variant="text"
                                color="buy"
                                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                                sx={{ textTransform: 'none' }}
                                onClick={onAddConditionClick}
                            >
                                Add condition
                            </Button>
                        </Box>
                    )}
                </Box>
            )}
        </Stack>

        {alert && alert.condition && !hideAlertMetadata && <>
            <Divider className={classes.divider} />
            <Stack direction="row">
                <label className={classes.label}>Trigger</label>
                <Box className={classes.value}>
                    <AlertTriggerInput onTriggerChange={onTriggerChange} trigger={alert.trigger}
                                       condition={alert.condition} />
                </Box>
            </Stack>
        </>}

        {alert && alert.expiry && !hideAlertMetadata && <>
            <Divider className={classes.divider} />
            <Stack direction={'row'} alignItems="flex-start">
                <label className={classes.label}>Expiration</label>
                <Box className={classes.value} sx={{ position: 'relative' }}>
                    <FormControl fullWidth size="small">
                        <Select
                            value={selectedExpiryOption}
                            onChange={(event) => onExpiryOptionChange(event.target.value as string)}
                            renderValue={(value) => {
                                if (value === CUSTOM_EXPIRY_ID) {
                                    const expiryLabel = dayjs(alert.expiry).format('MMM D, YYYY HH:mm')
                                    return `Custom date · ${expiryLabel}`
                                }
                                if (value === 'open_ended') {
                                    return (
                                        PRESET_EXPIRATION_OPTIONS.find((o) => o.id === 'open_ended')?.label ??
                                        'Open-ended'
                                    )
                                }
                                const preset = PRESET_EXPIRATION_OPTIONS.find((o) => o.id === value)
                                if (preset) {
                                    const expiryLabel = dayjs(alert.expiry).format('MMM D, YYYY HH:mm')
                                    return `${preset.label} · ${expiryLabel}`
                                }
                                return String(value)
                            }}
                            MenuProps={{
                                ...alertModalMenuProps,
                                PaperProps: { sx: { minWidth: 320 } },
                                anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                                transformOrigin: { vertical: 'top', horizontal: 'left' }
                            }}
                        >
                            {PRESET_EXPIRATION_OPTIONS.map((opt) => {
                                const now = dayjs()
                                return (
                                    <MenuItem key={opt.id} value={opt.id}>
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                gap: 2,
                                                width: '100%',
                                                pr: 0.5
                                            }}
                                        >
                                            <Typography component="span" variant="body2">
                                                {opt.label}
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                                sx={{ whiteSpace: 'nowrap', fontSize: 12 }}
                                            >
                                                {opt.secondary(now)}
                                            </Typography>
                                        </Box>
                                    </MenuItem>
                                )
                            })}
                            <MenuItem
                                value={CUSTOM_EXPIRY_ID}
                                onClick={() => {
                                    window.setTimeout(() => setCustomExpiryPickerOpen(true), 0)
                                }}
                                sx={(theme) => ({
                                    mt: 0.75,
                                    mx: 0.75,
                                    mb: 0.5,
                                    borderRadius: 1,
                                    py: 1,
                                    bgcolor: theme.palette.mode === 'dark' ? 'grey.300' : 'grey.200',
                                    color: 'common.black',
                                    '&:hover': {
                                        bgcolor: theme.palette.mode === 'dark' ? 'grey.400' : 'grey.300'
                                    }
                                })}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                        gap: 1
                                    }}
                                >
                                    <Typography variant="body2" fontWeight={600} sx={{ color: 'common.black' }}>
                                        Custom date
                                    </Typography>
                                    <CalendarMonthIcon sx={{ fontSize: 20, color: 'common.black' }} />
                                </Box>
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <DateTimePicker
                        open={customExpiryPickerOpen}
                        onClose={() => setCustomExpiryPickerOpen(false)}
                        value={dayjs(alert.expiry)}
                        onChange={onCustomExpiryAccept}
                        disableOpenPicker
                        closeOnSelect
                        ampm={false}
                        sx={{ p: 0, width: 0, height: 0, overflow: 'hidden', position: 'absolute' }}
                        slotProps={{
                            textField: {
                                size: 'small',
                                sx: { width: 0, height: 0, minWidth: 0, '& fieldset': { border: 'none' } }
                            },
                            popper: {
                                container: multiPaneFullScreen
                                    ? document.getElementById(MULTIPANE_FULLSCREEN_MODE_ID)
                                    : undefined
                            }
                        }}
                    />
                </Box>
            </Stack>
        </>}
        {alert && primaryCondition && !hideAlertMetadata && <>
            <Divider className={classes.divider} />

            <Stack direction="row" className={classes.row}>
                <label className={classes.label}>Alert Name</label>
                <TextField value={alert.name} size="small" className={classes.value} onChange={onNameChange} />
            </Stack>
            <Stack direction="row" alignItems="flex-start">
                <label className={classes.label}>Message</label>
                <Box sx={{ flex: 1, minWidth: 0 }} className={classes.value}>
                    <AlertMessageInput
                        condition={alert.condition}
                        message={alert.message}
                        symbolDisplayName={symbolsDisplayValue}
                        chartSnapshot={chartSnapshot}
                        chartTimeBucket={alert.timeBucket ?? chartSnapshot.timeBucket}
                        onChange={onMessageChange}
                        edit={edit}
                    />
                </Box>
            </Stack>
        </>}
    </Box>
}

export default memo(AlertSettingsTab)